// Tu Luyện `character.cultivation` (UI_SPEC §18.1), bố cục theo màn 1a của client tham khảo:
// caption (tên cảnh + cảnh giới) → khối đầu đúng hai hàng dưới caption: THANH tu vi x/y (số chính
// DUY NHẤT) và MỘT dòng trạng thái. Nơi bế quan và kiểu bế quan là hai điểm chạm trên tranh.
// Người tu luyện, khí hải và hạt linh khí vẽ trong Pixi (render/cultivationScene).
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '@i18n/t';
import { formatFull } from '@i18n/format';
import { CANH_MS, remainingText } from '@i18n/time';
import { stageLabel } from '@i18n/stage';
import { cultCost, lifespanMax, stepInfo } from '@sim/cultivation';
import {
  breakthroughPulse,
  cultivation,
  isAtPeak,
  lampRootFormed,
  onlineRate,
} from '@state/modules/cultivation';
import { now, onTick } from '@state/clock';
import { openWindow } from '@state/modules/ui';
import { NumBar } from '../../kit/NumBar';

const CANH_S = CANH_MS / 1000;

/** Nhịp đọc lại giờ cho dòng đếm ngược (đồng hồ UI 30s, UI_SPEC §10.6). */
function useClock(): number {
  const [n, setN] = useState(now());
  useEffect(() => onTick(setN), []);
  return n;
}

export function lifespanTone(age: number, max: number): '' | 'warn' | 'danger' {
  const r = age / max;
  return r >= 0.9 ? 'danger' : r >= 0.7 ? 'warn' : '';
}

export function CultivationScreen(): JSX.Element {
  const c = cultivation.value;
  const formed = lampRootFormed.value;
  const clock = useClock();
  const cost = cultCost(c.step);
  const peak = isAtPeak(c);
  const max = lifespanMax(c);
  const leftText = formatFull(Math.floor((max - c.lifespan.age) * 10 + 1e-6) / 10);
  const death = c.idle.seclusion.kind === 'death' ? c.idle.seclusion : null;
  const deathLeft = death?.endsAtMs ? death.endsAtMs - Math.max(clock, c.idle.lastSettledMs) : 0;
  const site = t(`${c.idle.locationId}.name`);
  const rate = formatFull(Math.round(onlineRate.value * CANH_S));
  // Chỉ rung thanh khi có đột phá SAU lúc vào màn (không rung lúc mới mở màn).
  const pulse = breakthroughPulse.value;
  const mountPulse = useRef(pulse);

  const status = !formed
    ? t('ui.cult.notFormed')
    : peak && stepInfo(c.step).realm === 1
      ? t('ui.cult.status.peak')
      : death
        ? t('ui.cult.status.death', { left: remainingText(deathLeft), rate })
        : c.idle.mode === 'trial' && c.idle.trialZoneId
          ? t('ui.cult.status.trial', { zone: `@${c.idle.trialZoneId}.name`, rate })
          : t('ui.cult.status.light', { rate });

  return (
    <>
      <div class="ht-caption" data-id="caption">
        <b>{t('ui.cult.caption')}</b>
        <span>
          <span data-id="cult-stage">{stageLabel(c.step)}</span>
          {' · '}
          <em class={`cult-life ${lifespanTone(c.lifespan.age, max)}`} data-id="cult-lifespan">
            {t('ui.cult.lifespan', { n: leftText })}
          </em>
        </span>
      </div>

      <div class="cult-idle">
        <NumBar
          id="cult-bar"
          class={pulse !== mountPulse.current ? 'shake' : undefined}
          value={c.cultivationXp}
          max={cost}
          shakeKey={pulse}
          ariaLabel={t('ui.cult.bar', {
            c: formatFull(Math.floor(c.cultivationXp)),
            max: formatFull(Math.round(cost)),
          })}
        />
        <small class={peak ? 'cult-status can' : 'cult-status'} data-id="cult-rate">
          {status}
        </small>
      </div>

      {formed && (
        <div class="cult-points">
          <button
            type="button"
            class="cult-point"
            data-id="hotspot-site"
            style={{ left: '110px', top: '318px' }}
            onClick={() => openWindow({ kind: 'site' })}
          >
            <i />
            <span>{site}</span>
          </button>
          <button
            type="button"
            class={death ? 'cult-point on' : 'cult-point'}
            data-id="hotspot-seclusion"
            style={{ left: '284px', top: '368px' }}
            onClick={() => openWindow({ kind: 'deathSeclusion' })}
          >
            <i />
            <span>
              {death ? t('cult.deathSeclusion') : t('cult.seclusionLight')}
              <small data-id="cult-seclusion-left">
                {death ? remainingText(deathLeft) : t('ui.cult.death.hint')}
              </small>
            </span>
          </button>
        </div>
      )}
    </>
  );
}
